import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { FILE_ANNOTATIONS_QUERY } from '../../graphql/queries';
import {
  CREATE_ANNOTATION_MUTATION,
  REPLY_ANNOTATION_MUTATION,
  RESOLVE_ANNOTATION_MUTATION,
  DELETE_ANNOTATION_MUTATION,
} from '../../graphql/mutations';
import styles from './PointCloudViewer.module.css';
import Icon from '../../components/common/Icon';

const TOKEN_KEY = 'geodraft_token';

type ViewerStatus = 'processing' | 'ready' | 'error';
type ColorMode = 'elevation' | 'rgb' | 'intensity';

interface AnnotationPosition { x: number; y: number; z: number; }
interface AnnotationUser { id: string; firstName: string; lastName: string; }
interface AnnotationReply { id: string; createdBy: AnnotationUser; text: string; createdAt: string; }
interface Annotation {
  id: string;
  createdBy: AnnotationUser;
  position: AnnotationPosition;
  text: string;
  status: string;
  replies: AnnotationReply[];
  createdAt: string;
}

const formatTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
    d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const PointCloudViewer = () => {
  const { projectId, fileId } = useParams<{ projectId: string; fileId: string }>();
  const navigate = useNavigate();

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [status, setStatus]         = useState<ViewerStatus>('processing');
  const [loaded, setLoaded]         = useState(false);
  const [loadError, setLoadError]   = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [pointSize, setPointSize]   = useState(1.0);
  const [colorMode, setColorMode]   = useState<ColorMode>('rgb');

  // Annotation state
  // Annotation state
  const [annotationMode, setAnnotationMode]   = useState(false);
  const [pendingPos, setPendingPos]           = useState<AnnotationPosition | null>(null);
  const [pendingText, setPendingText]         = useState('');
  const [activeId, setActiveId]               = useState<string | null>(null);
  const [expandedId, setExpandedId]           = useState<string | null>(null);
  const [replyText, setReplyText]             = useState('');
  const activeCardRef = useRef<HTMLDivElement>(null);

  // ── Fetch thumbnail once on mount ────────────────────────────────────────
  useEffect(() => {
    if (!fileId) return;
    const token = localStorage.getItem(TOKEN_KEY) || '';
    fetch(`/api/files/${fileId}/thumbnail`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.url) setThumbnailUrl(d.url); })
      .catch(() => {});
  }, [fileId]);

  // ── Poll conversion status ────────────────────────────────────────────────

  const pollStatus = useCallback(async (): Promise<ViewerStatus> => {
    const token = localStorage.getItem(TOKEN_KEY) || '';
    try {
      const res = await fetch(`/api/potree/status/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return 'error';
      const data: { status: string } = await res.json();
      return data.status === 'ready' ? 'ready' : data.status === 'error' ? 'error' : 'processing';
    } catch {
      return 'error';
    }
  }, [fileId]);

  useEffect(() => {
    if (!fileId) return;
    let interval: ReturnType<typeof setInterval>;
    const check = async () => {
      const s = await pollStatus();
      setStatus(s);
      if (s !== 'processing') clearInterval(interval);
    };
    check();
    interval = setInterval(check, 3000);
    return () => clearInterval(interval);
  }, [fileId, pollStatus]);

  // ── Annotations GraphQL ───────────────────────────────────────────────────

  const { data: annotationsData, refetch: refetchAnnotations } = useQuery(FILE_ANNOTATIONS_QUERY, {
    variables: { fileId },
    skip: !fileId || !loaded,
    fetchPolicy: 'cache-and-network',
  });
  const annotations: Annotation[] = annotationsData?.fileAnnotations ?? [];

  const [createAnnotation, { loading: creating }] = useMutation(CREATE_ANNOTATION_MUTATION, {
    onCompleted: () => { setPendingPos(null); setPendingText(''); refetchAnnotations(); },
  });

  const [replyToAnnotation, { loading: replying }] = useMutation(REPLY_ANNOTATION_MUTATION, {
    onCompleted: () => { setReplyText(''); refetchAnnotations(); },
  });

  const [resolveAnnotation] = useMutation(RESOLVE_ANNOTATION_MUTATION, {
    onCompleted: () => refetchAnnotations(),
  });

  const [deleteAnnotation] = useMutation(DELETE_ANNOTATION_MUTATION, {
    onCompleted: () => { setActiveId(null); setExpandedId(null); refetchAnnotations(); },
  });

  // ── Listen for messages from Potree iframe ────────────────────────────────

  const post = useCallback((cmd: Record<string, unknown>) => {
    iframeRef.current?.contentWindow?.postMessage(cmd, '*');
  }, []);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (!e.data || typeof e.data !== 'object') return;
      if (e.data.type === 'loaded') setLoaded(true);
      if (e.data.type === 'error') setLoadError(e.data.message || 'Unknown error');
      if (e.data.type === 'pointPicked') {
        setPendingPos(e.data.position as AnnotationPosition);
        setAnnotationMode(false);
        post({ type: 'setAnnotationMode', enabled: false });
      }
      if (e.data.type === 'markerClicked') {
        const id = e.data.id as string;
        setActiveId(id);
        setExpandedId(id);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [post]);

  // ── Sync markers into viewer whenever annotations change ──────────────────

  useEffect(() => {
    if (!loaded) return;
    post({ type: 'clearMarkers' });
    annotations.forEach((a, i) => {
      post({
        type: 'addMarker',
        id: a.id,
        position: a.position,
        index: i + 1,
        status: a.status,
        active: a.id === activeId,
      });
    });
  }, [annotations, loaded, post, activeId]);

  // Scroll to active annotation card
  useEffect(() => {
    if (activeId && activeCardRef.current) {
      activeCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeId]);

  // ── Annotation actions ────────────────────────────────────────────────────

  const toggleAnnotationMode = useCallback(() => {
    if (pendingPos) { setPendingPos(null); return; }
    const next = !annotationMode;
    setAnnotationMode(next);
    post({ type: 'setAnnotationMode', enabled: next });
  }, [annotationMode, pendingPos, post]);

  const handleSave = useCallback(() => {
    if (!pendingPos || !pendingText.trim() || !fileId) return;
    createAnnotation({ variables: { fileId, position: pendingPos, text: pendingText.trim() } });
  }, [pendingPos, pendingText, fileId, createAnnotation]);

  const handleReply = useCallback((annotationId: string) => {
    if (!replyText.trim()) return;
    replyToAnnotation({ variables: { annotationId, text: replyText.trim() } });
  }, [replyText, replyToAnnotation]);

  const handleResolve = useCallback((annotationId: string) => {
    resolveAnnotation({ variables: { annotationId } });
  }, [resolveAnnotation]);

  const handleDelete = useCallback((annotationId: string) => {
    deleteAnnotation({ variables: { annotationId } });
  }, [deleteAnnotation]);

  const handleCardClick = useCallback((id: string) => {
    setActiveId(id);
    setExpandedId(prev => prev === id ? null : id);
    post({ type: 'highlightMarker', id });
  }, [post]);

  // ── Viewer controls ───────────────────────────────────────────────────────

  const handlePointSize = (value: number) => { setPointSize(value); post({ type: 'setPointSize', value }); };
  const handleColorMode = useCallback((mode: ColorMode) => { setColorMode(mode); post({ type: 'setColorMode', value: mode }); }, [post]);
  const fitToScreen = useCallback(() => post({ type: 'fitToScreen' }), [post]);
  const zoomIn      = useCallback(() => post({ type: 'zoomIn' }), [post]);
  const zoomOut     = useCallback(() => post({ type: 'zoomOut' }), [post]);
  const resetView   = useCallback(() => post({ type: 'resetView' }), [post]);

  const token    = localStorage.getItem(TOKEN_KEY) || '';
  const viewerPage = (localStorage.getItem('geodraft_engine') === 'potree'
    || new URLSearchParams(window.location.search).get('engine') === 'potree')
    ? '/potree-viewer.html' : '/geo-viewer.html';
  const iframeSrc = status === 'ready'
    ? `${viewerPage}?fileId=${encodeURIComponent(fileId ?? '')}&token=${encodeURIComponent(token)}`
    : undefined;

  const openCount     = annotations.filter(a => a.status === 'open').length;
  const resolvedCount = annotations.filter(a => a.status === 'resolved').length;

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            <Icon name="arrow_back" style={{ fontSize: 18 }} />
            Back
          </button>
          <div className={styles.divider} />
          <div className={styles.title}>
            <Icon name="grain" style={{ fontSize: 18, color: 'rgba(255,255,255,0.4)' }} />
            Point Cloud Viewer
          </div>
        </div>

        <div className={styles.toolbar}>
          <button className={styles.toolButton} title="Zoom in" onClick={zoomIn} disabled={!loaded}>
            <Icon name="zoom_in" style={{ fontSize: 18 }} />
          </button>
          <button className={styles.toolButton} title="Zoom out" onClick={zoomOut} disabled={!loaded}>
            <Icon name="zoom_out" style={{ fontSize: 18 }} />
          </button>
          <button className={styles.toolButton} title="Fit to screen" onClick={fitToScreen} disabled={!loaded}>
            <Icon name="fit_screen" style={{ fontSize: 18 }} />
          </button>
          <button className={styles.toolButton} title="Reset view" onClick={resetView} disabled={!loaded}>
            <Icon name="3d_rotation" style={{ fontSize: 18 }} />
          </button>
          <div className={styles.divider} />
          <button
            className={`${styles.toolButton} ${(annotationMode || pendingPos) ? styles.toolButtonActive : ''}`}
            title={annotationMode ? 'Cancel — click anywhere to cancel' : 'Add annotation'}
            onClick={toggleAnnotationMode}
            disabled={!loaded}
          >
            <Icon name={annotationMode ? 'close' : 'comment'} style={{ fontSize: 18 }} />
          </button>
        </div>

        <div className={styles.headerRight}>
          <button className={styles.actionButton}>
            <Icon name="share" style={{ fontSize: 16 }} />
            Share
          </button>
          <button className={styles.actionButton}>
            <Icon name="download" style={{ fontSize: 16 }} />
            Download
          </button>
        </div>
      </header>

      {/* Body */}
      <div className={styles.body}>
        <div className={styles.canvasWrapper}>
          {annotationMode && (
            <div className={styles.pickHint}>
              <Icon name="ads_click" style={{ fontSize: 14 }} />
              Click on the point cloud to place an annotation
            </div>
          )}

          {iframeSrc && (
            <iframe
              ref={iframeRef}
              src={iframeSrc}
              className={`${styles.canvas} ${annotationMode ? styles.canvasPick : ''}`}
              style={{ border: 'none' }}
              title="Point Cloud Viewer"
            />
          )}

          {status === 'processing' && (
            <div className={styles.overlay} style={thumbnailUrl ? { background: 'rgba(10,12,20,0.7)' } : undefined}>
              {thumbnailUrl && <div className={styles.overlayThumb} style={{ backgroundImage: `url(${thumbnailUrl})` }} />}
              <div className={styles.overlayContent}>
                <div className={styles.overlayIcon}>
                  <Icon name="sync" style={{ fontSize: 40, color: 'rgba(245,158,11,0.6)' }} />
                </div>
                <p className={styles.overlayTitle} style={thumbnailUrl ? { color: '#fff' } : undefined}>File is Being Converted</p>
                <p className={styles.overlayText} style={thumbnailUrl ? { color: 'rgba(255,255,255,0.6)' } : undefined}>
                  The point cloud is being processed by the conversion service.
                  This page will update automatically when it&apos;s ready.
                </p>
                <div className={styles.progressBar}>
                  <div className={styles.progressFill} />
                </div>
              </div>
            </div>
          )}

          {status === 'ready' && !loaded && !loadError && (
            <div className={styles.overlay} style={thumbnailUrl ? { background: 'rgba(10,12,20,0.7)' } : undefined}>
              {thumbnailUrl && <div className={styles.overlayThumb} style={{ backgroundImage: `url(${thumbnailUrl})` }} />}
              <div className={styles.overlayContent}>
                <div className={styles.overlayIcon}>
                  <Icon name="downloading" style={{ fontSize: 40, color: 'rgba(99,102,241,0.6)' }} />
                </div>
                <p className={styles.overlayTitle} style={thumbnailUrl ? { color: '#fff' } : undefined}>Loading Point Cloud…</p>
                <div className={styles.progressBar}>
                  <div className={styles.progressFill} />
                </div>
              </div>
            </div>
          )}

          {(status === 'error' || loadError) && (
            <div className={styles.overlay}>
              <div className={styles.overlayContent}>
                <div className={styles.overlayIcon}>
                  <Icon name="cloud_off" style={{ fontSize: 40, color: 'rgba(239,68,68,0.6)' }} />
                </div>
                <p className={styles.overlayTitle}>Failed to Load</p>
                <div className={styles.errorBadge}>{loadError || 'Could not determine conversion status.'}</div>
              </div>
            </div>
          )}
        </div>

        {/* Side panel */}
        <aside className={styles.panel}>
          <div className={styles.panelSection}>
            <p className={styles.panelHeading}>File Info</p>
            <div className={styles.panelRow}>
              <p className={styles.panelLabel}>File ID</p>
              <p className={styles.panelValue}>{fileId ? `#${fileId.slice(-8)}` : '—'}</p>
            </div>
            <div className={styles.panelRow}>
              <p className={styles.panelLabel}>Project</p>
              <p className={styles.panelValue}>{projectId ? `#${projectId.slice(-8)}` : '—'}</p>
            </div>
            <div className={styles.panelRow}>
              <p className={styles.panelLabel}>Status</p>
              <span className={styles.statusDot}>
                <span className={`${styles.dot} ${status === 'ready' && loaded ? styles.dotReady : status === 'processing' ? styles.dotConverting : styles.dotError}`} />
                {status === 'ready' && loaded ? 'Loaded' : status === 'processing' ? 'Converting…' : 'Error'}
              </span>
            </div>
          </div>

          {loaded && (
            <>
              <div className={styles.panelSection}>
                <p className={styles.panelHeading}>Render</p>
                <div className={styles.panelRow}>
                  <p className={styles.panelLabel}>Point Size: {pointSize.toFixed(1)}</p>
                  <input
                    type="range"
                    className={styles.slider}
                    min={0.5} max={5} step={0.1}
                    value={pointSize}
                    onChange={(e) => handlePointSize(Number(e.target.value))}
                  />
                </div>
              </div>

              <div className={styles.panelSection}>
                <p className={styles.panelHeading}>Color Mode</p>
                <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                  {([
                    { key: 'rgb',       label: 'RGB' },
                    { key: 'elevation', label: 'Elevation' },
                    { key: 'intensity', label: 'Intensity' },
                  ] as { key: ColorMode; label: string }[]).map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => handleColorMode(key)}
                      style={{
                        flex: 1, padding: '4px 0', fontSize: 10,
                        fontWeight: colorMode === key ? 700 : 400,
                        background: colorMode === key ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.06)',
                        border: `1px solid ${colorMode === key ? 'rgba(99,102,241,0.6)' : 'transparent'}`,
                        borderRadius: 4,
                        color: colorMode === key ? '#a5b4fc' : 'rgba(255,255,255,0.4)',
                        cursor: 'pointer',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.panelSection}>
                <p className={styles.panelHeading}>Navigation</p>
                {[
                  { key: 'Left drag',  action: 'Orbit' },
                  { key: 'Right drag', action: 'Pan' },
                  { key: 'Scroll',     action: 'Zoom' },
                ].map(({ key, action }) => (
                  <div key={key} className={styles.panelRow} style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p className={styles.panelLabel}>{action}</p>
                    <kbd style={{ padding: '2px 6px', background: 'rgba(255,255,255,0.06)', borderRadius: 4, fontSize: 10, color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace' }}>
                      {key}
                    </kbd>
                  </div>
                ))}
              </div>

              {/* Annotations panel */}
              <div className={styles.panelSection} style={{ flex: 1, minHeight: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <p className={styles.panelHeading}>
                    Annotations
                    {annotations.length > 0 && (
                      <span className={styles.annotationCount}>{openCount} open · {resolvedCount} resolved</span>
                    )}
                  </p>
                </div>

                {/* New annotation form */}
                {pendingPos && (
                  <div className={styles.newAnnotationForm}>
                    <p className={styles.newAnnotationHint}>
                      <Icon name="location_on" style={{ fontSize: 12 }} />
                      Position captured — add a comment
                    </p>
                    <textarea
                      className={styles.annotationTextarea}
                      placeholder="Describe the issue or note…"
                      value={pendingText}
                      onChange={(e) => setPendingText(e.target.value)}
                      rows={3}
                      autoFocus
                    />
                    <div className={styles.annotationFormActions}>
                      <button
                        className={styles.cancelBtn}
                        onClick={() => { setPendingPos(null); setPendingText(''); }}
                      >
                        Cancel
                      </button>
                      <button
                        className={styles.saveBtn}
                        onClick={handleSave}
                        disabled={!pendingText.trim() || creating}
                      >
                        {creating ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Annotation list */}
                <div className={styles.annotationList}>
                  {annotations.length === 0 && !pendingPos && (
                    <p className={styles.emptyAnnotations}>
                      Click the <span style={{ fontWeight: 600 }}>comment</span> button in the toolbar, then click anywhere on the point cloud to add an annotation.
                    </p>
                  )}

                  {annotations.map((a, i) => (
                    <div
                      key={a.id}
                      ref={a.id === activeId ? activeCardRef : null}
                      className={`${styles.annotationCard} ${a.id === activeId ? styles.annotationCardActive : ''} ${a.status === 'resolved' ? styles.annotationCardResolved : ''}`}
                      onClick={() => handleCardClick(a.id)}
                    >
                      <div className={styles.annotationCardHeader}>
                        <span className={styles.annotationIndex}>{i + 1}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p className={styles.annotationAuthor}>
                            {a.createdBy.firstName} {a.createdBy.lastName}
                          </p>
                          <p className={styles.annotationTime}>{formatTime(a.createdAt)}</p>
                        </div>
                        {a.status === 'resolved' && (
                          <span className={styles.resolvedBadge}>Resolved</span>
                        )}
                      </div>

                      <p className={styles.annotationText}>{a.text}</p>

                      {/* Replies */}
                      {a.replies.length > 0 && (
                        <div className={styles.replies}>
                          {a.replies.map((r) => (
                            <div key={r.id} className={styles.replyItem}>
                              <p className={styles.replyAuthor}>{r.createdBy.firstName} {r.createdBy.lastName}</p>
                              <p className={styles.replyText}>{r.text}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Expanded actions */}
                      {expandedId === a.id && (
                        <div className={styles.annotationActions} onClick={(e) => e.stopPropagation()}>
                          <textarea
                            className={styles.annotationTextarea}
                            placeholder="Add a reply…"
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            rows={2}
                          />
                          <div className={styles.annotationFormActions}>
                            <button
                              className={styles.resolveBtn}
                              onClick={() => handleResolve(a.id)}
                            >
                              {a.status === 'resolved' ? 'Reopen' : 'Resolve'}
                            </button>
                            <div style={{ flex: 1 }} />
                            <button
                              className={styles.deleteBtn}
                              onClick={() => handleDelete(a.id)}
                              title="Delete annotation"
                            >
                              <Icon name="delete" style={{ fontSize: 14 }} />
                            </button>
                            <button
                              className={styles.saveBtn}
                              onClick={() => handleReply(a.id)}
                              disabled={!replyText.trim() || replying}
                            >
                              Reply
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </aside>
      </div>
    </div>
  );
};

export default PointCloudViewer;
